import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react';

const CLOSE_PARENT_MENU_EVENT = 'closeSiblingParentMenuItems';
export const PARENT_MENU_ITEM_DATA_ATTRIBUTE = 'data-parent-menu-item';

export const useParentMenuHelper = ({
  menuItemRef,
  sticky,
  onKeyDown,
}: {
  menuItemRef: React.RefObject<HTMLLIElement | null>;
  sticky?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const shouldFocusOnExitRef = useRef<boolean>(true);

  // Close other parent menus at the same level when opening the menu, we are querying the dom directly because currently AppContextMenu is not aware of its children, so we can't manage it on that level
  const closeOtherParentMenus = useCallback(() => {
    const currentItem = menuItemRef.current;
    if (!currentItem) return;

    // Find the parent menu container (the direct parent UL/menu)
    const parentMenu = currentItem.closest('[role="menu"]');
    if (!parentMenu) return;

    // Find all parent menu items that are direct children of the same parent menu
    const siblingParentMenuItems = Array.from(parentMenu.children).filter(
      (child) => child.getAttribute(PARENT_MENU_ITEM_DATA_ATTRIBUTE) === 'true' && child !== currentItem,
    );

    // Close only sibling parent menus
    siblingParentMenuItems.forEach((item) => {
      if (item instanceof HTMLElement) {
        item.dispatchEvent(new CustomEvent(CLOSE_PARENT_MENU_EVENT));
      }
    });
  }, [menuItemRef]);

  const open = useCallback(() => {
    shouldFocusOnExitRef.current = true;
    closeOtherParentMenus();
    setIsOpen(true);
  }, [closeOtherParentMenus]);

  const close = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  // Debounced open with 100ms delay
  const debouncedOpen = useMemo(() => debounce(open, 100, { leading: false, trailing: true }), [open]);

  // Debounced close with 100ms delay
  const debouncedClose = useMemo(() => debounce(close, 100, { leading: false, trailing: true }), [close]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOpen.cancel();
      debouncedClose.cancel();
    };
  }, [debouncedOpen, debouncedClose]);

  // Listen for close events from other parent menus
  useEffect(() => {
    const menuItem = menuItemRef.current;
    if (!menuItem) return;

    menuItem.addEventListener(CLOSE_PARENT_MENU_EVENT, close);
    return () => {
      menuItem.removeEventListener(CLOSE_PARENT_MENU_EVENT, close);
    };
  }, [close, menuItemRef]);

  // Listen for hover events on sibling menu items and closing the menu if the sibling menu item is hovered.
  useEffect(() => {
    if (sticky) return;

    const menuItem = menuItemRef.current;
    if (!menuItem) return;

    // Find the parent menu container
    const parentMenu = menuItem.closest('[role="menu"]');
    if (!parentMenu) return;

    // Listen for mouseenter on all sibling menu items
    const handleMouseEnterOnSibling = (e: Event) => {
      const target = e.target as HTMLElement;
      // If a sibling menu item (not this one) is hovered, close this submenu
      if (target.tagName === 'LI' && target !== menuItem && target.closest('[role="menu"]') === parentMenu && isOpen) {
        shouldFocusOnExitRef.current = false;
        close();
      }
    };

    parentMenu.addEventListener('mouseenter', handleMouseEnterOnSibling, true);

    return () => {
      parentMenu.removeEventListener('mouseenter', handleMouseEnterOnSibling, true);
    };
  }, [isOpen, close, menuItemRef, sticky]);

  const handleItemKeyDown = useCallback(
    (e: KeyboardEvent) => {
      onKeyDown?.(e);
      if ((e.key !== 'ArrowRight' && e.key !== 'Enter') || e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      open();
    },
    [onKeyDown, open],
  );

  const handleMenuKeyDown = useCallback((e: KeyboardEvent) => {
    e.stopPropagation();
    if ((e.key !== 'ArrowLeft' && e.key !== 'Escape') || e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) {
      return;
    }

    e.preventDefault();
    setIsOpen(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Cancel pending close
    debouncedClose.cancel();
    // Open with debounce
    debouncedOpen();
  }, [debouncedOpen, debouncedClose]);

  const handleMouseLeave = useCallback(() => {
    // Cancel pending open
    debouncedOpen.cancel();

    // Close with debounce
    if (!sticky) {
      debouncedClose();
    }
  }, [debouncedOpen, debouncedClose, sticky]);

  return {
    close,
    isOpen,
    shouldFocusOnExit: shouldFocusOnExitRef.current,
    handleMouseEnter,
    handleMouseLeave,
    handleItemKeyDown,
    handleMenuKeyDown,
  };
};
